import { Fragment, type ReactNode } from "react";
import { IconX } from "@tabler/icons-react";
import { Badge, Button, Group, Popover, PopoverContent, PopoverTrigger, Td, Text, Tr } from "@/components/ui";
import type { Area, OrganizacionAdmin, UsuarioAdmin } from "@/lib/types";
import { PanelEdicionUsuario } from "./PanelEdicionUsuario";

/**
 * Lista de badges colapsada · muestra las primeras `max` y agrupa el resto en
 * un chip "+N" que abre un Popover con TODAS. Evita el "muro de badges" que
 * inflaba las filas de la tabla de usuarios (11+ áreas / 7 organizaciones).
 */
function BadgesColapsadas({
  badges,
  max = 3,
  resumen,
}: {
  badges: { key: string; node: ReactNode }[];
  max?: number;
  resumen: string;
}) {
  const visibles = badges.slice(0, max);
  const restantes = badges.length - max;
  return (
    <Group gap={4} className="flex-wrap">
      {visibles.map((b) => (
        <Fragment key={b.key}>{b.node}</Fragment>
      ))}
      {restantes > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Ver ${restantes} ${resumen} más`}
              className="inline-flex h-5 items-center rounded-sm border border-border bg-muted px-2 text-xs font-semibold text-muted-foreground transition-colors hover:bg-secondary"
            >
              +{restantes}
            </button>
          </PopoverTrigger>
          <PopoverContent className="max-w-xs">
            <div className="flex flex-wrap gap-1.5">
              {badges.map((b) => (
                <Fragment key={b.key}>{b.node}</Fragment>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </Group>
  );
}

/**
 * Fila de usuario para la tabla desktop · 7 columnas + expansión a una
 * fila con el panel de edición. La fila expandida ocupa `colSpan={7}`.
 */
export function UsuarioRow({
  u,
  expandida,
  onToggle,
  todasAreas,
  todasOrganizaciones,
}: {
  u: UsuarioAdmin;
  expandida: boolean;
  onToggle: () => void;
  todasAreas: Area[];
  todasOrganizaciones: OrganizacionAdmin[];
}) {
  return (
    <>
      <Tr className="align-top">
        <Td>
          <Text size="xs" className="font-mono">
            {u.email}
          </Text>
        </Td>
        <Td>
          <Text size="sm">{u.fullName ?? "—"}</Text>
        </Td>
        <Td>
          <Group gap={4} className="flex-wrap">
            {u.roles.map((r) => (
              <Badge key={r} variant="light" color="info" size="sm">
                {r}
              </Badge>
            ))}
          </Group>
        </Td>
        <Td>
          {u.areas.length === 0 ? (
            <Text size="xs" c="dimmed">
              —
            </Text>
          ) : (
            <BadgesColapsadas
              max={3}
              resumen="áreas"
              badges={u.areas.map((a) => ({
                key: a,
                // Tono NEUTRO (slate) · el naranja se reserva para acciones/marca.
                node: (
                  <Badge variant="light" color="slate" size="sm" uppercase={false}>
                    {a}
                  </Badge>
                ),
              }))}
            />
          )}
        </Td>
        <Td>
          {u.organizaciones.length === 0 ? (
            <Text size="xs" c="dimmed">
              Todas (admin/auditor)
            </Text>
          ) : (
            <BadgesColapsadas
              max={2}
              resumen="organizaciones"
              badges={u.organizaciones.map((o) => ({
                key: o.id,
                node: (
                  <Badge variant="light" color="exito" size="sm" title={`Sociedad SAP ${o.numeroSociedad}`}>
                    {o.nombre} <span className="font-mono normal-case">{o.numeroSociedad}</span>
                  </Badge>
                ),
              }))}
            />
          )}
        </Td>
        <Td>
          <Badge variant="light" color={u.activo ? "exito" : "ambar"}>
            {u.activo ? "Activo" : "Bloqueado"}
          </Badge>
        </Td>
        <Td align="right" className="whitespace-nowrap">
          <Button
            variant={expandida ? "filled" : "default"}
            size="xs"
            leftSection={expandida ? <IconX size={12} /> : undefined}
            onClick={onToggle}
          >
            {expandida ? "Cerrar" : "Editar"}
          </Button>
        </Td>
      </Tr>
      {expandida ? (
        <Tr className="bg-muted/40">
          <Td colSpan={7}>
            <PanelEdicionUsuario u={u} todasAreas={todasAreas} todasOrganizaciones={todasOrganizaciones} />
          </Td>
        </Tr>
      ) : null}
    </>
  );
}
